// Minimal realtime WebSocket client for the Tinsmith dashboard.
//
// Opens a single `/api/v1/realtime` WebSocket (lazily, on first use) and
// multiplexes terminal PTY byte streams over it. See specifications/realtime.md
// and specifications/terminals.md for the wire format.
//
// Terminal wire format:
// - Subscribe:   JSON  { type: "subscribe",      scopes: ["terminal:<id>"] }
// - Unsubscribe: JSON  { type: "unsubscribe",    scopes: ["terminal:<id>"] }
// - Resize:      JSON  { type: "terminal.resize", terminal: "<id>", cols, rows }
// - Input:       binary  4-byte big-endian channel id + raw bytes
// - Output:      binary  4-byte big-endian channel id + raw bytes (server → us)
//
// Channel ids are never sent by the server. It assigns them per connection,
// monotonically from 1, in the exact order the client subscribes, and never
// reuses one within a connection. This client mirrors that allocation with its
// own counter so inbound frames map back to the right terminal and outbound
// frames carry the id the server expects. On reconnect the server's counter
// restarts at 1, so we reset ours and re-subscribe every open terminal in the
// original order.

const PING_INTERVAL_MS = 25_000;
const RECONNECT_MAX_MS = 2_000;
const RECONNECT_MIN_MS = 250;

// A realtime event delivered on a subscribed scope (task:/repo:/workspace:…).
// Event scopes do NOT consume a per-connection channel id (only terminal/
// tunnel scopes do), so they never perturb the terminal channel counter.
export type RealtimeEvent = {
  readonly kind: string;
  readonly ref?: unknown;
  readonly scope: string;
  readonly seq?: number;
  readonly summary?: unknown;
};

export type ScopeListener = (event: RealtimeEvent) => void;

export type OpenTerminalOptions = {
  readonly onData: (bytes: Uint8Array) => void;
};

export type TerminalHandle = {
  readonly close: () => void;
  readonly resize: (cols: number, rows: number) => void;
  readonly send: (bytes: Uint8Array) => void;
};

// Per-terminal state. A class (not a `type`) so the channel id and last size
// can be mutated in place as the connection cycles.
class TerminalEntry {
  channelId?: number;
  cols?: number;
  rows?: number;

  constructor(
    readonly terminalPublicId: string,
    readonly scope: string,
    readonly onData: (bytes: Uint8Array) => void,
  ) {}
}

const realtimeUrl = (): string =>
  `${globalThis.location.origin.replace(/^http/, "ws")}/api/v1/realtime`;

export class RealtimeClientImpl {
  // Liveness: set when a ping is sent, cleared by any inbound frame. If a whole
  // ping interval passes with it still set, the socket died silently (no
  // `onclose`) and we force a reconnect (realtime.md §Failure modes).
  private awaitingPong = false;
  private readonly byChannel = new Map<number, TerminalEntry>();
  private channelSeq = 0;
  private pingTimer: ReturnType<typeof setInterval> | undefined;
  private ready = false;
  private reconnectDelay = RECONNECT_MIN_MS;
  private reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  private readonly scopeListeners = new Map<string, Set<ScopeListener>>();
  // Last seq seen per event scope. On (re)subscribe we send this so the server
  // replays events missed during a WS blip (specifications/realtime.md §Resume).
  private readonly scopeSeq = new Map<string, number>();
  private socket: WebSocket | undefined;
  private readonly terminals = new Map<string, TerminalEntry>();

  // Subscribe to realtime JSON events on a scope (e.g. `task:<id>`,
  // `repo:<id>`). Returns an unsubscribe fn. Multiple listeners may share a
  // scope; the WS subscribe/unsubscribe is sent only on the first/last one.
  subscribeScope(scope: string, onEvent: ScopeListener): () => void {
    let set = this.scopeListeners.get(scope);
    const isNew = !set;
    if (!set) {
      set = new Set();
      this.scopeListeners.set(scope, set);
    }
    set.add(onEvent);
    this.ensureConnected();
    if (isNew && this.ready) {
      this.sendSubscribe([scope]);
    }
    return () => {
      const listeners = this.scopeListeners.get(scope);
      if (!listeners) {
        return;
      }
      listeners.delete(onEvent);
      if (listeners.size === 0) {
        this.scopeListeners.delete(scope);
        this.sendJson({ scopes: [scope], type: "unsubscribe" });
      }
    };
  }

  // Subscribe to a terminal's byte stream. Returns a handle to feed keystrokes,
  // report resizes, and tear the subscription down.
  openTerminal(
    terminalPublicId: string,
    options: OpenTerminalOptions,
  ): TerminalHandle {
    // Drop any stale channel mapping if this terminal is re-opened without an
    // intervening close (the counter never rewinds).
    const existing = this.terminals.get(terminalPublicId);
    if (existing?.channelId !== undefined) {
      this.byChannel.delete(existing.channelId);
    }
    const entry = new TerminalEntry(
      terminalPublicId,
      `terminal:${terminalPublicId}`,
      options.onData,
    );
    this.terminals.set(terminalPublicId, entry);

    this.ensureConnected();
    if (this.ready) {
      this.subscribe(entry);
    }

    return {
      close: () => this.closeTerminal(terminalPublicId),
      resize: (cols, rows) => this.resizeTerminal(terminalPublicId, cols, rows),
      send: (bytes) => this.sendBytes(terminalPublicId, bytes),
    };
  }

  private closeTerminal(terminalPublicId: string): void {
    const entry = this.terminals.get(terminalPublicId);
    if (!entry) {
      return;
    }
    this.terminals.delete(terminalPublicId);
    if (entry.channelId !== undefined) {
      this.byChannel.delete(entry.channelId);
    }
    this.sendJson({ scopes: [entry.scope], type: "unsubscribe" });
  }

  private connect(): void {
    this.reconnectTimer = undefined;
    if (typeof WebSocket === "undefined") {
      return;
    }
    let socket: WebSocket;
    try {
      socket = new WebSocket(realtimeUrl());
    } catch {
      this.scheduleReconnect();
      return;
    }
    socket.binaryType = "arraybuffer";
    this.socket = socket;
    socket.onclose = () => this.onClose();
    socket.onerror = () => socket.close();
    socket.onmessage = (ev) => this.onMessage(ev);
  }

  private ensureConnected(): void {
    if (this.socket || this.reconnectTimer) {
      return;
    }
    this.connect();
  }

  private onBinary(buffer: ArrayBuffer): void {
    if (buffer.byteLength < 4) {
      return;
    }
    const channelId = new DataView(buffer).getUint32(0, false);
    this.byChannel.get(channelId)?.onData(new Uint8Array(buffer, 4));
  }

  private onClose(): void {
    this.socket = undefined;
    this.ready = false;
    this.byChannel.clear();
    this.stopPing();
    this.scheduleReconnect();
  }

  private onJson(text: string): void {
    let msg: {
      bytes_b64?: string;
      from_seq?: number;
      kind?: string;
      scope?: string;
      seq?: number;
      since_seq?: Record<string, number>;
      terminal?: string;
      type?: string;
    };
    try {
      msg = JSON.parse(text) as typeof msg;
    } catch {
      return;
    }
    switch (msg.type) {
      case "event":
        if (typeof msg.scope === "string") {
          if (typeof msg.seq === "number") {
            this.trackSeq(msg.scope, msg.seq);
          }
          this.emitToScope(msg.scope, msg as RealtimeEvent);
        }
        return;
      case "ready":
        this.onReady();
        return;
      case "resync":
        // The gap was too large to replay — reset our seq and tell listeners to
        // reload from scratch (they refetch on any event).
        if (typeof msg.scope === "string") {
          this.scopeSeq.set(msg.scope, msg.from_seq ?? 0);
          this.emitToScope(msg.scope, { kind: "resync", scope: msg.scope });
        }
        return;
      case "scope.revoked":
        // Either a subscribe we sent was denied, or — since the server sweeps
        // live subscriptions when a membership changes (realtime.md
        // §Authorisation) — access was withdrawn under an active one. Drop the
        // resume cursor either way: it is a claim on replay for a scope we no
        // longer hold, and it would be sent back on the next subscribe.
        if (typeof msg.scope === "string") {
          this.scopeSeq.delete(msg.scope);
          this.emitToScope(msg.scope, {
            kind: "scope.revoked",
            scope: msg.scope,
          });
        }
        return;
      case "subscribed":
        // Adopt the server's high-water as our baseline for each scope so a
        // later resume asks from the right point.
        if (msg.since_seq) {
          for (const [scope, hw] of Object.entries(msg.since_seq)) {
            this.trackSeq(scope, hw);
          }
        }
        return;
      case "terminal.snapshot":
        // The current-screen backlog for a (re)subscribed terminal — write it to
        // the emulator before the live byte stream resumes. See terminals.md.
        if (
          typeof msg.terminal === "string" &&
          typeof msg.bytes_b64 === "string"
        ) {
          this.terminals.get(msg.terminal)?.onData(decodeBase64(msg.bytes_b64));
        }
        return;
      default:
        return;
    }
  }

  private emitToScope(scope: string, event: RealtimeEvent): void {
    const listeners = this.scopeListeners.get(scope);
    if (!listeners) {
      return;
    }
    for (const fn of [...listeners]) {
      try {
        fn(event);
      } catch {
        // Listener errors don't propagate.
      }
    }
  }

  private trackSeq(scope: string, seq: number): void {
    this.scopeSeq.set(scope, Math.max(this.scopeSeq.get(scope) ?? 0, seq));
  }

  // Subscribe to event scopes, carrying `since_seq` for any we already have a
  // seq for (a resume); scopes with no prior seq subscribe fresh (no replay).
  private sendSubscribe(scopes: ReadonlyArray<string>): void {
    if (scopes.length === 0) {
      return;
    }
    const since_seq: Record<string, number> = {};
    for (const scope of scopes) {
      const seq = this.scopeSeq.get(scope);
      if (seq !== undefined) {
        since_seq[scope] = seq;
      }
    }
    this.sendJson({ scopes: [...scopes], since_seq, type: "subscribe" });
  }

  private onMessage(ev: MessageEvent): void {
    // Any inbound frame (pong, event, or terminal bytes) proves the socket is
    // alive, so the ping watchdog can rearm.
    this.awaitingPong = false;
    if (typeof ev.data === "string") {
      this.onJson(ev.data);
      return;
    }
    if (ev.data instanceof ArrayBuffer) {
      this.onBinary(ev.data);
    }
  }

  private onReady(): void {
    this.ready = true;
    this.reconnectDelay = RECONNECT_MIN_MS;
    // A fresh connection restarts the server's channel counter at 1, so reset
    // ours and re-subscribe every open terminal in insertion order.
    this.channelSeq = 0;
    this.byChannel.clear();
    for (const entry of this.terminals.values()) {
      entry.channelId = undefined;
      this.subscribe(entry);
    }
    // Re-subscribe event scopes (no channel id, so order doesn't matter),
    // carrying our last-seen seq so the server replays anything missed while
    // the socket was down.
    this.sendSubscribe([...this.scopeListeners.keys()]);
    this.startPing();
  }

  private resizeTerminal(
    terminalPublicId: string,
    cols: number,
    rows: number,
  ): void {
    const entry = this.terminals.get(terminalPublicId);
    if (!entry) {
      return;
    }
    entry.cols = cols;
    entry.rows = rows;
    this.sendJson({
      cols,
      rows,
      terminal: terminalPublicId,
      type: "terminal.resize",
    });
  }

  private scheduleReconnect(): void {
    // Only reconnect while a terminal or an event scope still wants the socket.
    if (
      (this.terminals.size === 0 && this.scopeListeners.size === 0) ||
      this.reconnectTimer
    ) {
      return;
    }
    const delay = this.reconnectDelay;
    this.reconnectDelay = Math.min(this.reconnectDelay * 2, RECONNECT_MAX_MS);
    this.reconnectTimer = setTimeout(() => this.connect(), delay);
  }

  private sendBytes(terminalPublicId: string, bytes: Uint8Array): void {
    const entry = this.terminals.get(terminalPublicId);
    if (
      !entry ||
      entry.channelId === undefined ||
      !this.socket ||
      this.socket.readyState !== WebSocket.OPEN
    ) {
      // Not subscribed yet (still provisioning) or the socket is down.
      return;
    }
    const frame = new Uint8Array(4 + bytes.length);
    new DataView(frame.buffer).setUint32(0, entry.channelId, false);
    frame.set(bytes, 4);
    this.socket.send(frame);
  }

  private sendJson(payload: Record<string, unknown>): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(payload));
    }
  }

  private startPing(): void {
    this.stopPing();
    this.awaitingPong = false;
    this.pingTimer = setInterval(() => {
      if (this.awaitingPong) {
        // The previous ping got no response within a full interval — the
        // connection is dead. Closing triggers onClose → reconnect/backoff.
        this.socket?.close();
        return;
      }
      this.awaitingPong = true;
      this.sendJson({ type: "ping" });
    }, PING_INTERVAL_MS);
  }

  private stopPing(): void {
    if (this.pingTimer !== undefined) {
      clearInterval(this.pingTimer);
      this.pingTimer = undefined;
    }
  }

  private subscribe(entry: TerminalEntry): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return;
    }
    entry.channelId = ++this.channelSeq;
    this.byChannel.set(entry.channelId, entry);
    this.sendJson({ scopes: [entry.scope], type: "subscribe" });
    if (entry.cols !== undefined && entry.rows !== undefined) {
      this.sendJson({
        cols: entry.cols,
        rows: entry.rows,
        terminal: entry.terminalPublicId,
        type: "terminal.resize",
      });
    }
  }
}

export const RealtimeClient = new RealtimeClientImpl();

function decodeBase64(b64: string): Uint8Array {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) {
    bytes[i] = bin.charCodeAt(i);
  }
  return bytes;
}
